import React, { useState, useEffect, useCallback } from "react";
import { useParams } from 'react-router-dom';
import DivineQuotesService from "../Services/DivineQuotes";
import ListPagination from "../Components/ListPagination";
import { LogInModel } from "./LoginModel";
import Loader from "./Loader";
import "../Styles/DivineQuote.css";
import { useTranslation } from "react-i18next";
import imgdownload from "../assets/audioPlayer/img/gradient.svg";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import Reset from "../Images/reset.png";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const DivineQuotesPage = () => {
    const { t } = useTranslation();
    const [divinequotes, setDivineQuotes] = useState<any[] | undefined>(
        undefined
    );
    const [refresh, setRefresh] = useState(false);
    const [SortValue, setSortValue] = useState("5");
    const UserIdentity = localStorage.getItem("UserId") as any;
    const [pagination, setPagination] = useState({
        pageNo: 0,
        recordsPerPage: 20,
        totalRecords: 0,
    });
    const [logIn, setLogIn] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");
    const [loginState, setLoginState] = useState<boolean>(false);
    const [isCarouselOpen, setIsCarouselOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [divineQuoteName, setDivineQuoteName] = useState<any>();
    const [paramId, setParamId] = useState<string | null>(null);
    const params = useParams<{ id?: string }>();
    const paramFromUrl = params?.id;
    const [isInitialParamView, setIsInitialParamView] = useState<boolean>(() => !!paramFromUrl);
    const [columnCount, setColumnCount] = useState<number>(4);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 640) setColumnCount(1);
            else if (window.innerWidth < 1024) setColumnCount(2);
            else if (window.innerWidth < 1280) setColumnCount(3);
            else setColumnCount(4);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const masonryColumns = Array.from({ length: columnCount }, (_, i) =>
        divinequotes?.filter((_, index) => index % columnCount === i)
    );

    const closeModal = () => {
        setLogIn(false);
    };
    const handleLoginStateChange = (newState: any) => {
        setLoginState(newState === true);
    };

    const showSnackbar = (message: string, severity: "success" | "error" | "info" | "warning") => {
        setSnackbarOpen(false);

        setTimeout(() => {
            setSnackbarMessage(message);
            setSnackbarSeverity(severity);
            setSnackbarOpen(true);
        }, 50);
    };

    // capture id param from URL (e.g. /divinequotes/:id) and store in state
    useEffect(() => {
        if (paramFromUrl) {
            setParamId(paramFromUrl);
            setIsInitialParamView(true);
            // optionally fetch a single divine quote by id:
            // DivineQuotesService.getDivineQuotesId(paramFromUrl).then(res => { /* handle single item */ });
            console.log("DivineQuotesPage param id:", paramFromUrl);
        }
    }, [paramFromUrl]);

    useEffect(() => {
        // setRefresh(false);
        const fetchData = async () => {
            try {
                if (isInitialParamView && paramId) {
                    // fetch single item by id
                    const response = await DivineQuotesService.getDivineQuotesId(paramId);
                    const res = await response;
                    const item = res?.result;
                    if (item) {
                        setDivineQuotes([item]);
                        setPagination(prev => ({ ...prev, totalRecords: 1 }));
                        // open popup for the item
                        setCurrentImageIndex(0);
                        setDivineQuoteName(item?.name);
                        setIsCarouselOpen(true);
                    } else {
                        setDivineQuotes([]);
                        setPagination(prev => ({ ...prev, totalRecords: 0 }));
                    }
                } else {
                    // fetch list as before
                    const response = await DivineQuotesService.getDivineQuotes(
                        pagination.pageNo === 0
                            ? 0
                            : pagination.recordsPerPage * pagination.pageNo - 20,
                        pagination.recordsPerPage, SortValue
                    );
                    const res = await response;

                    setDivineQuotes(res.result?.items);

                    setPagination(prev => ({
                        ...prev,
                        totalRecords: res.result?.totalRecords,
                    }));
                }
            } catch (error) {
                console.error("Error fetching data:", error);

                showSnackbar("Failed to fetch divine quotes. Please try again later.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [refresh, pagination.pageNo, pagination.recordsPerPage, isInitialParamView, paramId, SortValue]);

    const getFileExtension = (url: string): string | null => {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const extension = pathname.split('.').pop()?.toLowerCase();
            return extension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension) ? extension : null;
        } catch {
            return null;
        }
    };

    const handleDownload = async (url: any, name: string) => {
        setLoading(true);

        try {
            // Fetch the image directly from your server
            const imageResponse = await fetch(url, {
                method: 'GET'
            });

            if (!imageResponse.ok) {
                showSnackbar("Downloading failed. Status: " + imageResponse.status, "error");
                return;
            }

            const blob = await imageResponse.blob();
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(blob);
            // Use the divine quote name as filename, with proper extension
            const fileExtension = getFileExtension(url) || 'jpg';
            // sanitize and append extension
            const sanitized = (name || 'download').toString().replace(/\s+/g, '_');
            const fileName = sanitized + (sanitized.endsWith('.' + fileExtension) ? '' : '.' + fileExtension);
            downloadLink.download = fileName;

            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(downloadLink.href);

            showSnackbar("Image downloaded successfully!", "success");

        } catch (err) {
            showSnackbar("Downloading failed: " + (err instanceof Error ? err.message : "An unknown error occurred."), "error");
            console.error("Download error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    // useEffect(() => {
    //     window.scrollTo(0, 0);
    // }, []);

    const openCarousel = (index: number) => {
        setCurrentImageIndex(index);
        setIsCarouselOpen(true);
    };

    const closeCarousel = useCallback(() => {
        setIsCarouselOpen(false);
        // If page started in initial param view, closing should show the full list
        if (isInitialParamView) {
            setIsInitialParamView(false);
            setParamId(null);
            // trigger a refresh to load the list view
            setRefresh(prev => !prev);
        }
    }, [isInitialParamView]);

    const goToPreviousImage = useCallback(() => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? divinequotes!.length - 1 : prevIndex - 1
        );
    }, [divinequotes]);

    const goToNextImage = useCallback(() => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === divinequotes!.length - 1 ? 0 : prevIndex + 1
        );
    }, [divinequotes]);

    function ResetData() {
        setPagination({
            ...pagination,
            pageNo: 0,
        });
        setRefresh(prev => !prev);
    }

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isCarouselOpen || !divinequotes || divinequotes.length === 0) {
                return;
            }

            if (event.key === "ArrowLeft") {
                goToPreviousImage();
            } else if (event.key === "ArrowRight") {
                goToNextImage();
            } else if (event.key === "Escape") {
                closeCarousel();
            }
        };

        if (isCarouselOpen) {
            document.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isCarouselOpen, divinequotes, goToPreviousImage, goToNextImage]);

    // useEffect(() => {
    //     ResetData();
    // }, [pagination.pageNo]);

    return (
        <>
            <div
                className="breadcrumbs-head newcontainer"
                style={{
                    width: "100%",
                    marginTop: "-175px",
                    background: "none",
                    backgroundColor: "#ffedbc",
                    height: "240px",
                    borderBottom: "2px solid #fff",
                    paddingTop: 0,
                }}
            >
                <div className="breadcrumbs">
                    <div
                        className="containers"
                        style={{
                            fontSize: "36px",
                            fontWeight: 700,
                            color: "rgb(209, 21, 1)",
                            top: "155px",
                            fontFamily: "ChanakyaUni",
                        }}
                    >
                        <div
                            style={{
                                fontFamily: "ChanakyaUni,NalandaTim,Tunga",
                                fontSize: "36px",
                                fontStyle: "normal",
                                fontWeight: "700",
                                color: "#d11501",
                            }}
                        >
                            {t("Amrit_Vachan_tr")}
                        </div>
                        <div
                            style={{
                                fontSize: "19px",
                                fontWeight: 600,
                                color: "#2d2a29",
                                marginTop: "-8px",
                            }}
                        >
                            <Link
                                style={{ marginRight: "4px", color: "#2d2a29" }}
                                to="/"
                            >
                                {t("Home_tr")}
                            </Link>
                            <span style={{ color: "#2d2a29" }}>
                                <span>/ {t("Amrit_Vachan_tr")}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="fontfamily"
                style={{
                    userSelect: "none",
                    backgroundColor: "#fff6e1",
                    marginTop: -3,
                    paddingTop: "1px",
                }}
            >

                <div className="containers">
                    <div
                        className="gst-page-content"
                        style={{
                            display: "block",
                            padding: "25px 15px 0",
                            borderRadius: "4px",
                            background: "#fff6e1",
                            boxShadow: "0 0 7px 1px #f5deb1",
                            fontFamily: "ChanakyaUni",
                            height: "100%",
                            margin: "15px 0 0 0",
                        }}
                    >
                        <div className="row" style={{ margin: "5px 0px" }}>
                            <div className="filter1 col-6" style={{ margin: "0px 0px" }}>
                                <span>
                                    {t("Total_Records_tr")} : {pagination.totalRecords}
                                </span>
                            </div>
                            <div className="filter1 col-6" style={{ textAlign: "right" }} >
                                <span>{t("Sort_tr")}</span>
                                <select
                                    style={{ backgroundColor: "rgb(255, 242, 213)" }}
                                    value={SortValue}
                                    onChange={(e) => {
                                        setSortValue(e.target.value);
                                    }}
                                >
                                    <option value="4">{t("ascending_date_tr")}</option>
                                    <option value="5">{t("descending_date_tr")}</option>
                                </select>
                            </div>
                        </div>

                        {loading ? (
                            <div className="ebooks-category resultnotfound">
                                <Loader loading={loading} />
                            </div>
                        ) :
                            divinequotes && divinequotes.length > 0 ? (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "20px",
                                        width: "100%",
                                        backgroundColor: "#fff6e1",
                                    }}
                                >
                                    {masonryColumns.map((column, colIndex) => (
                                        <div
                                            key={colIndex}
                                            style={{
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "20px"
                                            }}
                                        >
                                            {column?.map((divquote: any, itemIndex: number) => {
                                                const quoteNumber = divquote?.name?.match(/\d+/)?.[0] || "";
                                                return (
                                                    <div
                                                        key={`${colIndex}-${itemIndex}`}
                                                        style={{
                                                            boxSizing: "border-box",
                                                            border: "1px solid #f7ebce",
                                                            borderRadius: "15px",
                                                            backgroundColor: "#fff",
                                                            overflow: "hidden",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            boxShadow: "0 4px 8px rgba(0,0,0,0.05)"
                                                        }}>
                                                        <img
                                                            src={divquote?.quotesThumbPath}
                                                            alt="Divine Quote Thumbnail"
                                                            onClick={() => {
                                                                const absoluteIndex = divinequotes!.findIndex(q => q.id === divquote.id);
                                                                openCarousel(absoluteIndex);
                                                                setDivineQuoteName(divquote?.name);
                                                            }}
                                                            style={{
                                                                width: "100%",
                                                                height: "auto",
                                                                cursor: "pointer",
                                                                display: "block",
                                                                borderTopLeftRadius: '15px',
                                                                borderTopRightRadius: '15px'
                                                            }}
                                                        />
                                                        <div style={{ padding: "15px", flex: 1, display: "flex", flexDirection: "column" }}>
                                                            {divquote?.quotesMessage && (
                                                                <p style={{
                                                                    fontFamily: 'ChanakyaUni',
                                                                    fontSize: "18px",
                                                                    marginBottom: "8px",
                                                                    lineHeight: "1.4",
                                                                    color: "#333",
                                                                    textAlign: "left"
                                                                }}>
                                                                    {divquote?.quotesMessage}
                                                                </p>
                                                            )}
                                                            <div style={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                gap: "2px",
                                                                marginTop: "auto",
                                                                paddingTop: "0"
                                                            }}>
                                                                <div style={{
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: "8px",
                                                                    marginTop: "auto"
                                                                }}>
                                                                    <div style={{
                                                                        width: "100%",
                                                                        height: "1px",
                                                                        backgroundColor: "#1a237e"
                                                                    }} />

                                                                    <h4 style={{
                                                                        fontFamily: 'ChanakyaUni',
                                                                        fontSize: "16px",
                                                                        margin: 0,
                                                                        color: "#1a237e",
                                                                        textAlign: "left"
                                                                    }}>
                                                                        {t("Daily_Divine_Quote_No_tr")} {quoteNumber}
                                                                    </h4>

                                                                    <span style={{
                                                                        fontSize: "14px",
                                                                        color: "#666",
                                                                        fontFamily: 'ChanakyaUni',
                                                                        alignSelf: "flex-start"
                                                                    }}>
                                                                        {t("Date_tr")} {divquote?.quotesStrDate}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="ebooks-category resultnotfound">
                                    {t("result_not_found_tr")} {/* 🔥 CHANGED: show only when not loading */}
                                </div>
                            )}
                    </div>
                    <div className="col-12" style={{ marginTop: "30px", padding: "0 0 3% 0", display: "flex" }}>
                        <ListPagination
                            totalRecords={pagination.totalRecords}
                            recordsPerPage={pagination.recordsPerPage}
                            onClick={(p) => {
                                setPagination({
                                    ...pagination,
                                    pageNo: p,
                                });
                                // setLoading(true);
                                setRefresh(prev => !prev);
                            }}
                        />
                        {/* <div>
                            <span className="boxheadtitle">{t("Reset_tr")}</span>
                            <img
                                title="reset"
                                alt="reset"
                                src={Reset}
                                className="resetimg"
                                onClick={ResetData}
                            />
                        </div> */}
                    </div>
                </div>
            </div>
            {isCarouselOpen && divinequotes && divinequotes.length > 0 && (
                <div
                    onClick={closeCarousel}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <button
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); closeCarousel(); }}
                        style={{
                            position: "absolute",
                            top: "20px",
                            right: "20px",
                            background: "none",
                            border: "none",
                            color: "white",
                            fontSize: "30px",
                            cursor: "pointer",
                            zIndex: 1001,
                        }}
                    >
                        &times;
                    </button>
                    <button
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleDownload(divinequotes?.[currentImageIndex]?.quotesPath, divinequotes?.[currentImageIndex]?.name); }}
                        style={{
                            position: "absolute",
                            top: "20px",
                            right: "60px",
                            background: "none",
                            border: "none",
                            color: "white",
                            fontSize: "30px",
                            cursor: "pointer",
                            zIndex: 1001,
                        }}
                    >
                        <FontAwesomeIcon icon={faDownload} style={{ fontSize: '20px' }} />
                    </button>
                    <div
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                        }}
                    >
                        <img
                            src={divinequotes?.[currentImageIndex]?.quotesPath}
                            alt={`Divine Quote ${currentImageIndex + 1}`}
                            style={{
                                height: "90vh",
                                width: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px',
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            onError={(e) => {
                                e.currentTarget.src = "https://placehold.co/800x600/CCCCCC/FFFFFF?text=Image+Not+Found";
                            }}
                        />
                    </div>

                    {/* Show prev/next only when not viewing a single item via initial URL param */}
                    {!isInitialParamView && (
                        <>
                            <button
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); goToPreviousImage(); }}
                                style={{
                                    background: "rgba(255, 255, 255, 0.5)",
                                    border: "none",
                                    padding: "10px 15px",
                                    cursor: "pointer",
                                    fontSize: "24px",
                                    color: "#333",
                                    position: 'absolute',
                                    left: "20px",
                                    borderRadius: "5px",
                                    zIndex: 1001,
                                    transform: "translateY(-50%)",
                                    top: "50%",
                                }}
                            >
                                &#10094;
                            </button>

                            <button
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); goToNextImage(); }}
                                style={{
                                    background: "rgba(255, 255, 255, 0.5)",
                                    border: "none",
                                    padding: "10px 15px",
                                    cursor: "pointer",
                                    fontSize: "24px",
                                    color: "#333",
                                    position: 'absolute',
                                    right: "20px",
                                    borderRadius: "5px",
                                    zIndex: 1001,
                                    transform: "translateY(-50%)",
                                    top: "50%",
                                }}
                            >
                                &#10095;
                            </button>
                        </>
                    )}
                </div>
            )}

            <LogInModel opens={logIn} onCloses={closeModal} onLoginStateChange={handleLoginStateChange} />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%', borderRadius: '8px' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default DivineQuotesPage;